import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import CreateByLawsAndRegulationsForm, {
  LawsAndRegulationsForm,
} from '@/views/pages/dynamicList/CreateByLawsAndRegulationsForm.vue';
import CreateByMaterialsForm from '@/views/pages/dynamicList/CreateByMaterialsForm.vue';
import CreateByRequestsForm, {
  RequestsForm,
} from '@/views/pages/dynamicList/CreateByRequestsForm.vue';
import { Company } from '@/interfaces/vuex/company';
import { SdsMaximumUpload } from '@/lib/enum';
import { readAssertedCompany } from '@/store/main/getters';
import BaseFormCard from '@/views/base/BaseFormCard.vue';
import { store } from '@/store/index';
import {
  dispatchCreateDynamicList,
  dispatchRequestDynamicList,
} from '@/store/main/actionsMain';
import { useDynamicListComposable } from '@/composables/useDynamicList';
import { usePermissionComposable } from '@/composables/usePermission';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useChemicanUrls } from '@/composables/useChemicanUrls';

// NOTE: RequestsForm type now includes lawRegulationIds for old UI compatibility


export default /*#__PURE__*/_defineComponent({
  __name: 'ListCreateDialog',
  props: {
    value: { type: Boolean },
    company: null,
    initialMode: null
  },
  emits: ["input", "created"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (e: 'created', skipNotification?: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  company: Company;
  initialMode?: '' | 'byLawsAndRegulations' | 'byMaterials' | 'byRequests';
};

const $t = i18n.t.bind(i18n);

// 共通の URL 生成ロジックを使用
const { contactUrl } = useChemicanUrls();

const m = defineMessages({
  selectMode: 'チェックリストの作成モードを選択してください',
  requestChemican: '対象法令がない、または細かい条件をケミカンに依頼する',
  paidPlanInfo:
    '※有料プランでは、チェックリストの基本的なルールはお客様ご自身で作成いただけます。さらに、複雑で詳細なカスタマイズが必要な場合は、ご依頼いただくことが可能です。',
  upgradeInfo: '※有料プランへのアップグレードは',
  here: 'こちら',
  next: '次へ進む',
  sendToChemican: 'ケミカンへ依頼を送信する',
  selectLawsAndRegulations: '対象法令 / 細則の選択',
  createChecklist: 'チェックリストを作成',
  confirmChecklist: 'チェックリスト内容の確認',
  requestToChemican: 'ケミカンに依頼',
  confirmRequest: '依頼内容の確認',
  containsRequlatedBy: '{shortLaws}の規制対象物質を含む可能性のあるSDS',
  listSummary: '{longLaws}の規制対象物質を含む可能性のあるSDS',
  applicableLaws: '該当法令： {fullLaws}',
  targetRegulations: '対象規則： {shortRegulations}',
  extractionConditionSingle: '抽出条件：以下に該当するSDSを抽出',
  extractionConditionMultiple: '抽出条件：以下いずれかに該当するSDSを抽出',
  conditionSection15:
    '{index}. SDS15章（適用法令）に、{shortLaws}に該当すると考えられる内容の記載があるSDS',
  conditionSection3Cas:
    '{index}. SDS3章（組成及び成分情報）に、{shortLaws}対象物質に指定されている物質のCAS番号が記載されているSDS',
  conditionSection3Material:
    '{index}. SDS3章（組成及び成分情報）に、{shortLaws}対象物質に指定されている対象物質名と完全に同じ物質名が記載されているSDS',
  notes: '注意事項：',
  source:
    '出典： NITE-CHRIP（ https://www.nite.go.jp/chem/chrip/chrip_search/sltLst ）',
  comma: '、',
});



const dialogValue = computed({
  get: () => props.value,
  set: (value) => emit('input', value),
});

const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

const current = ref<{
  mode: '' | 'byLawsAndRegulations' | 'byMaterials' | 'byRequests';
  step: number;
}>({
  mode: '',
  step: 1,
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal && props.initialMode) {
      current.value.mode = props.initialMode;
      current.value.step = props.initialMode === '' ? 1 : 2;
    }
  }
);

const form = ref<{
  byLawsAndRegulations: LawsAndRegulationsForm;
  byMaterials?: unknown;
  byRequests: RequestsForm;
}>({
  byLawsAndRegulations: {
    lawRegulationIds: [],
    extractionTargets: [],
    title: '',
    description: '',
  },
  byRequests: {
    lawRegulationIds: [],
    description: '',
  },
});

const isLoading = ref(false);
const isValidForm = ref(false);
const formRef = ref();

const { getLawText, getRegulationText, getLawRegulationText } =
  useDynamicListComposable();

const dialogTitle = computed(() => {
  if (current.value.mode === '') {
    return $t(m.selectMode);
  }
  if (current.value.mode === 'byLawsAndRegulations') {
    if (current.value.step === 1) {
      return $t(sharedMessages.createFromLaws);
    }
    if (current.value.step === 2) {
      return $t(m.selectLawsAndRegulations);
    }
    if (current.value.step === 3) {
      return $t(m.createChecklist);
    }
    if (current.value.step === 4) {
      return $t(m.confirmChecklist);
    }
  }

  if (current.value.mode === 'byMaterials') {
    return $t(sharedMessages.createFromMaterials);
  }
  if (current.value.mode === 'byRequests') {
    if (current.value.step === 2) {
      return $t(
        props.initialMode
          ? sharedMessages.requestChecklistConsultation
          : m.requestToChemican
      );
    }
    if (current.value.step === 3) {
      return $t(m.confirmRequest);
    }
  }
  return '';
});

const closeDialog = () => {
  dialogValue.value = false;
  current.value.mode = '';
  current.value.step = 1;
  resetForm();
};

const resetForm = (step = 1) => {
  if (step < 2) {
    form.value.byLawsAndRegulations.lawRegulationIds = [];
    form.value.byLawsAndRegulations.extractionTargets = [];
    form.value.byRequests.lawRegulationIds = [];
    form.value.byRequests.description = '';
  }
  if (step < 3) {
    form.value.byLawsAndRegulations.title = '';
    form.value.byLawsAndRegulations.description = '';
  }
};

const updateByRequestsForm = (updatedForm: Partial<RequestsForm>) => {
  if (updatedForm.lawRegulationIds !== undefined) {
    form.value.byRequests.lawRegulationIds = updatedForm.lawRegulationIds;
  }
  if (updatedForm.description !== undefined) {
    form.value.byRequests.description = updatedForm.description;
  }
};

const updateByLawsAndRegulationsForm = (
  updatedForm: LawsAndRegulationsForm
) => {
  form.value.byLawsAndRegulations = updatedForm;
};

const hasSubscription = computed((): boolean => {
  return (
    readAssertedCompany(store)['maximum_upload'] >
    SdsMaximumUpload.DEFAULT.value
  );
});

const handleCreateByLawsAndRegulationsClick = () => {
  current.value.mode = 'byLawsAndRegulations';
  current.value.step = 2;
};
const handleCreateByMaterialsClick = () => {
  current.value.mode = 'byMaterials';
  current.value.step = 2;
};
const handleCreateByRequestsClick = () => {
  current.value.mode = 'byRequests';
  current.value.step = 2;
};

const handlePrevStepClick = () => {
  current.value.step -= 1;
  if (current.value.step === 1) {
    current.value.mode = '';
  }
  resetForm(current.value.step);
};

const handleNextStepClick = () => {
  current.value.step += 1;
  if (
    current.value.mode === 'byLawsAndRegulations' &&
    current.value.step === 3
  ) {
    form.value.byLawsAndRegulations.title = generateLawsAndRegulationsTitle();
    form.value.byLawsAndRegulations.description =
      generateLawsAndRegulationsDescription();
  }
};

const handleSubmitByLawsAndRegulations = async () => {
  isLoading.value = true;
  try {
    const {
      lawRegulationIds,
      extractionTargets,
      title,
      description,
      dbt_types,
      law_ids,
      regulation_ids,
    } = form.value.byLawsAndRegulations;
    await dispatchCreateDynamicList(store, {
      lawRegulationIds,
      extractionTargets,
      title,
      description,
      dbt_types,
      law_ids,
      regulation_ids,
    });
    emit('created');
    closeDialog();
  } finally {
    isLoading.value = false;
  }
};

const handleSubmitByRequests = async () => {
  const description = form.value.byRequests.description.trim();
  if (!description) return;
  isLoading.value = true;
  try {
    const company = readAssertedCompany(store);
    const payload = {
      companyName: company.name,
      description,
      law_regulation_text: props.initialMode
        ? []
        : form.value.byRequests.lawRegulationIds.map(
            (lawRegulationId) =>
              `${getLawRegulationText(lawRegulationId)}(${lawRegulationId})`
          ),
      skipNotification: !!props.initialMode,
    };
    await dispatchRequestDynamicList(store, payload);
    emit('created', !!props.initialMode);
    closeDialog();
  } finally {
    isLoading.value = false;
  }
};

const generateLawsAndRegulationsTitle = () => {
  const { lawRegulationIds } = form.value.byLawsAndRegulations;
  const shortLaws = lawRegulationIds
    .map((lawRegulationId) => {
      const lawId = lawRegulationId.split('-')[0];
      return getLawText(lawId, 'short');
    })
    .join($t(m.comma));
  return $t(m.containsRequlatedBy, { shortLaws: shortLaws.trim() });
};

const generateLawsAndRegulationsDescription = () => {
  const { lawRegulationIds, extractionTargets } =
    form.value.byLawsAndRegulations;
  const lawSet: Set<string> = new Set();
  const regulationSet: Set<string> = new Set();
  const comma = $t(m.comma);

  lawRegulationIds.forEach((lawRegulationId) => {
    const [lawId, regulationId] = lawRegulationId.split('-');
    lawSet.add(lawId);
    if (regulationId !== 'all') {
      regulationSet.add(regulationId);
    }
  });

  const shortLaws = Array.from(lawSet)
    .map((lawId) => getLawText(lawId, 'short'))
    .join(comma);
  const longLaws = Array.from(lawSet)
    .map((lawId) => getLawText(lawId, 'long'))
    .join(comma);
  const fullLaws = Array.from(lawSet)
    .map((lawId) => getLawText(lawId, 'full'))
    .join(comma);
  const shortRegulations = Array.from(regulationSet)
    .map((regulationId) => getRegulationText(regulationId))
    .join(comma);

  let extractionConditions: string[] = [];
  let conditionIndex = 1;

  if (extractionTargets.includes('section15')) {
    extractionConditions.push(
      $t(m.conditionSection15, { index: conditionIndex, shortLaws })
    );
    conditionIndex++;
  }
  if (extractionTargets.includes('section3Cas')) {
    extractionConditions.push(
      $t(m.conditionSection3Cas, { index: conditionIndex, shortLaws })
    );
    conditionIndex++;
  }
  if (extractionTargets.includes('section3Material')) {
    extractionConditions.push(
      $t(m.conditionSection3Material, { index: conditionIndex, shortLaws })
    );
    conditionIndex++;
  }

  return `
${$t(m.listSummary, { longLaws })}
${$t(m.applicableLaws, { fullLaws })}
${$t(m.targetRegulations, { shortRegulations })}
${$t(
  extractionConditions.length > 1
    ? m.extractionConditionMultiple
    : m.extractionConditionSingle
)}
${extractionConditions.join('\n')}
${$t(m.notes)}
${$t(m.source)}
 `.trim();
};

return { __sfc: true,$t, contactUrl, m, props, emit, dialogValue, hasPermission, P, current, form, isLoading, isValidForm, formRef, getLawText, getRegulationText, getLawRegulationText, dialogTitle, closeDialog, resetForm, updateByRequestsForm, updateByLawsAndRegulationsForm, hasSubscription, handleCreateByLawsAndRegulationsClick, handleCreateByMaterialsClick, handleCreateByRequestsClick, handlePrevStepClick, handleNextStepClick, handleSubmitByLawsAndRegulations, handleSubmitByRequests, generateLawsAndRegulationsTitle, generateLawsAndRegulationsDescription, CreateByLawsAndRegulationsForm, CreateByMaterialsForm, CreateByRequestsForm, BaseFormCard, sharedMessages }
}

})